import { useCallback } from 'react';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { Strings } from '../constants/Strings';
import { logger } from '../utils/logger';
import { showErrorMessage } from '../utils/messageToast';
import { dispatchNativePermissionCheck } from '../lib/session/nativeMessaging';
import { fixImageOrientation } from '../utils/fixImageOrientation';

const strings = Strings.get();

interface UseImagePickerProps {
  onImageSelected: (imageUrl: string, file: File) => void;
}

/**
 * Web-native image picker using HTML5 file input
 * Supports both camera and library on mobile browsers
 */
export const useImagePicker = ({ onImageSelected }: UseImagePickerProps) => {
  const processFile = useCallback(async (file: File) => {
    try {
      // Normalize orientation for preview + upload
      const correctedFile = await fixImageOrientation(file);
      const imageUrl = URL.createObjectURL(correctedFile);

      onImageSelected(imageUrl, correctedFile);

      /* ANALYTICS_DISABLED: UPDATE_USER_ATTEMPT ('update_user_attempt')
        analyticsService.trackEvent(AnalyticsEvent.UPDATE_USER_ATTEMPT, {
                source: 'file_input',
                action: 'profile_photo_selected',
              });
        */
    } catch (error) {
      logger.error('Image picker error:', error);
      showErrorMessage(strings.introduction.errors.uploadFailed);
    }
  }, [onImageSelected]);

  const handleChooseFromLibrary = useCallback(() => {
    dispatchNativePermissionCheck('library', 'public_profile_library');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = false;
    input.style.display = 'none';
    input.style.position = 'absolute';
    document.body.appendChild(input);

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        input.remove();
        return;
      }
      try {
        await processFile(file);
      } finally {
        input.remove();
      }
    };

    // Ensure reuse even for same file selection
    input.value = '';
    input.click();
  }, [processFile]);

  const handleTakePhoto = useCallback(() => {
    dispatchNativePermissionCheck('camera', 'public_profile_camera');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    // @ts-ignore - capture is valid on mobile browsers
    input.capture = 'environment'; // Request camera on mobile
    input.style.display = 'none';
    input.style.position = 'absolute';
    document.body.appendChild(input);

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        input.remove();
        return;
      }
      try {
        await processFile(file);
      } finally {
        input.remove();
      }
    };

    input.value = '';
    input.click();
  }, [processFile]);

  return {
    handleChooseFromLibrary,
    handleTakePhoto,
  };
}; 